import React from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { FertilityTestingStackScreenProps } from "../FertilityTestingNavigator";
import type { FertilityTestingStackParamList } from "../FertilityTestingNavigator";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import {
  TestTube2,
  Baby,
  Sparkles,
  type LucideIcon,
} from "lucide-react-native";
import type {
  TestResultCreate,
  UrineTestCategory,
} from "@louise/medical-history/basic-info/types";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { getNextTestScreen } from "../getNextTestScreen";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";

type UrineTestType = "ovulation" | "pregnancy" | "other";

const urineTests: {
  id: UrineTestType;
  labelKey: string;
  icon: LucideIcon;
  sex: "AFAB" | "AMAB" | "both";
}[] = [
  {
    id: "ovulation",
    labelKey: "med_history.fertility.testing.urine_type.ovulation",
    icon: TestTube2,
    sex: "AFAB",
  },
  {
    id: "pregnancy",
    labelKey: "med_history.fertility.testing.urine_type.pregnancy",
    icon: Baby,
    sex: "AFAB",
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.urine_type.other",
    icon: Sparkles,
    sex: "both",
  },
];

export function UrineTestsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const filteredTests = urineTests.filter(
    (test) => test.sex === "both" || test.sex === state.sex,
  );
  const navigation =
    useNavigation<
      FertilityTestingStackScreenProps<"UrineTests">["navigation"]
    >();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();

  // Find existing urine test result in state
  const urineTestResult = state.testResults.find((tr) => tr.type === "urine");

  // Map category to UI type
  const categoryToUI: Record<string, UrineTestType> = {
    ovulation_kit: "ovulation",
    urinalysis: "other",
    other: "other",
  };

  const selection: UrineTestType | null = urineTestResult?.urineTestCategory
    ? (categoryToUI[urineTestResult.urineTestCategory] ?? null)
    : null;

  const handleSelection = (testType: UrineTestType) => {
    const categoryMap: Record<UrineTestType, string> = {
      ovulation: "ovulation_kit",
      pregnancy: "urinalysis",
      other: "other",
    };

    const newTestResult: TestResultCreate = {
      ...urineTestResult,
      type: "urine" as const,
      urineTestCategory: categoryMap[testType] as UrineTestCategory,
    };

    const updatedResults = urineTestResult
      ? state.testResults.map((tr) =>
          tr.type === "urine" ? newTestResult : tr,
        )
      : [...state.testResults, newTestResult];

    updateState({ testResults: updatedResults });
  };

  const handleNext = () => {
    const newIndex = (state.currentNavigationIndex || 0) + 1;
    updateState({ currentNavigationIndex: newIndex });
    const nextScreen = getNextTestScreen({
      ...state,
      currentNavigationIndex: newIndex,
    });
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.urine_title")}
        subtitle={t("med_history.fertility.testing.urine_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleNext}
        saveDisabled={!selection}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {filteredTests.map((test) => {
            const isSelected = selection === test.id;
            return (
              <View key={test.id} style={styles.optionWrapper}>
                <SquareOptionButton
                  icon={test.icon}
                  label={t(test.labelKey)}
                  isSelected={isSelected}
                  onPress={() => handleSelection(test.id)}
                  iconSize={20}
                  containerSize={40}
                  labelSize="sm"
                />
              </View>
            );
          })}
        </View>
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default UrineTestsScreen;
